import {
  Button,
  Card,
  CardBody,
  Chip,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from '@heroui/react';
import React from 'react';
import { FiRotateCcw } from 'react-icons/fi';

import { GroupUploadResult, GroupUploadStatus } from '../types';

const STATUS_DISPLAY: Record<
  GroupUploadStatus,
  {
    label: string;
    color: 'default' | 'primary' | 'success' | 'danger' | 'warning';
  }
> = {
  pending: { label: 'Waiting', color: 'default' },
  validating: { label: 'Validating…', color: 'primary' },
  ready: { label: 'Validated', color: 'primary' },
  uploading: { label: 'Uploading…', color: 'primary' },
  success: { label: 'Uploaded', color: 'success' },
  failed: { label: 'Failed', color: 'danger' },
  skipped: { label: 'Skipped — nothing written', color: 'warning' },
};

const chatLabel = (result: GroupUploadResult): string => {
  const { chatInfo } = result.group;
  const name = chatInfo?.chatName ?? chatInfo?.restaurantName;
  const parts = [name, chatInfo?.supplierName].filter(Boolean);
  return parts.length > 0 ? parts.join(' — ') : result.group.chatId;
};

const summaryLabel = (result: GroupUploadResult): string => {
  if (!result.summary) return '—';
  const { newItems, updatedItems, removedItems, createdCustomItems } =
    result.summary;
  const parts = [
    `${newItems} added`,
    `${updatedItems} updated`,
    `${removedItems} removed`,
  ];
  if (createdCustomItems > 0)
    parts.push(`${createdCustomItems} custom created`);
  return parts.join(', ');
};

interface UploadResultsProps {
  results: GroupUploadResult[];
  isUploading: boolean;
  onReset: () => void;
}

export const UploadResults: React.FC<UploadResultsProps> = ({
  results,
  isUploading,
  onReset,
}) => {
  const itemErrors = results.flatMap((result) =>
    (result.summary?.errors ?? []).map((itemError) => ({
      result,
      itemError,
    }))
  );

  return (
    <Card>
      <CardBody className="space-y-4">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold text-foreground">
            {isUploading ? 'Uploading…' : 'Upload results'}
          </h2>
          {isUploading && <Spinner size="sm" />}
        </div>

        <Table
          aria-label="Upload results per order guide"
          removeWrapper
          isStriped
        >
          <TableHeader>
            <TableColumn>CHAT</TableColumn>
            <TableColumn>STATUS</TableColumn>
            <TableColumn>RESULT</TableColumn>
          </TableHeader>
          <TableBody>
            {results.map((result) => {
              const status = STATUS_DISPLAY[result.status];
              return (
                <TableRow key={result.group.key}>
                  <TableCell>
                    <div>{chatLabel(result)}</div>
                    <div className="text-xs text-default-400 font-mono">
                      {result.group.chatId}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Chip color={status.color} variant="flat" size="sm">
                      {status.label}
                    </Chip>
                  </TableCell>
                  <TableCell>
                    {result.status === 'failed'
                      ? (result.errorMessage ?? 'Unknown error')
                      : summaryLabel(result)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        {itemErrors.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-base font-semibold text-foreground">
              Rejected products
            </h3>
            <Table
              aria-label="Products rejected by the backend"
              removeWrapper
              isStriped
            >
              <TableHeader>
                <TableColumn>CHAT</TableColumn>
                <TableColumn>PRODUCT</TableColumn>
                <TableColumn>UNIT</TableColumn>
                <TableColumn>REASON</TableColumn>
              </TableHeader>
              <TableBody>
                {itemErrors.map(({ result, itemError }, index) => (
                  <TableRow key={`${result.group.key}-${index}`}>
                    <TableCell>{chatLabel(result)}</TableCell>
                    <TableCell>
                      {itemError.name ?? itemError.productCode ?? '—'}
                      {itemError.productCode && (
                        <span className="text-xs text-default-400 font-mono ml-2">
                          {itemError.productCode}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>{itemError.unit}</TableCell>
                    <TableCell>
                      {itemError.reason}
                      <span className="text-xs text-default-400 ml-2">
                        ({itemError.errorCode})
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {!isUploading && (
          <Button
            variant="flat"
            startContent={<FiRotateCcw />}
            onPress={onReset}
          >
            Upload another file
          </Button>
        )}
      </CardBody>
    </Card>
  );
};
